import React from 'react';
import { Alert, Box, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';

export const REASON_MIN_LENGTH = 10;

function ResolutionResponsibleFields({
  candidates,
  isLoadingCandidates,
  candidatesError,
  responsibleUserId,
  onResponsibleChange,
  reason,
  onReasonChange,
  disabled = false,
}) {
  const candidateList = Array.isArray(candidates) ? candidates : [];
  const hasNoCandidate = candidateList.length === 0;
  const hasSingleCandidate = candidateList.length === 1;
  const hasMultipleCandidates = candidateList.length > 1;

  const needsSelection = hasMultipleCandidates;
  const isSelectionMissing = needsSelection && !responsibleUserId;
  const trimmedReason = (reason || '').trim();
  const isReasonTooShort = trimmedReason.length < REASON_MIN_LENGTH;

  const candidatesIntro = hasSingleCandidate
    ? 'Laut Schichtprotokoll war zu diesem Zeitpunkt verantwortlich:'
    : 'In Frage kommen diese Mitarbeiter:innen — eine:r davon war verantwortlich:';

  const candidatesOutro = hasSingleCandidate
    ? 'Sprich den Ablauf zeitnah und konkret mit dieser Person nach, damit der Prozess wieder zuverlässig sitzt. Dein Eintrag unten wird mitprotokolliert, damit wir Muster früh erkennen und gezielt gegensteuern können.'
    : 'Wähle unten aus, wer es war, und sprich den Ablauf zeitnah und konkret nach, damit der Prozess wieder zuverlässig sitzt. Dein Eintrag wird mitprotokolliert, damit wir Muster früh erkennen und gezielt gegensteuern können.';

  return (
    <Stack spacing={2}>
      <Alert severity='warning' icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
        {isLoadingCandidates ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant='body2'>Ermittle verantwortliche Person(en) …</Typography>
          </Box>
        ) : candidatesError ? (
          <Typography variant='body2'>
            Mögliche Verantwortliche konnten nicht automatisch ermittelt werden.
            Bitte kläre im Team, wer zuständig war, und schule gezielt nach.
          </Typography>
        ) : (
          <Box>
            <Typography variant='subtitle2' component='div' sx={{ fontWeight: 700, mb: 0.75 }}>
              Das darf nicht zur Routine werden.
            </Typography>

            <Typography variant='body2' component='div' sx={{ mb: 1 }}>
              Check-in, Kassieren und Auschecken sind <strong>Kernprozesse</strong> —
              davon hängt ab, wer tatsächlich im Haus ist, ob die Einnahmen bei uns
              ankommen und ob eine Buchung sauber abgeschlossen ist. Jede
              Nacherfassung verfälscht kurzfristig unsere Zahlen, kostet Zeit und
              kann im Ernstfall Geld oder Kundenvertrauen kosten. Deshalb muss sie
              die Ausnahme bleiben — nicht der Regelfall.
            </Typography>

            {hasNoCandidate ? (
              <Typography variant='body2' component='div'>
                Im Schichtprotokoll lässt sich für diese Buchung niemand konkret
                zuordnen. Bitte kläre im Team, wer hätte handeln müssen, und
                schule gezielt nach. Dein Eintrag unten wird mitprotokolliert,
                damit wir Muster früh erkennen und gezielt gegensteuern können.
              </Typography>
            ) : (
              <>
                <Typography variant='body2' component='div' sx={{ mb: 0.5 }}>
                  {candidatesIntro}
                </Typography>

                <Box sx={{ my: 1.25 }}>
                  {candidateList.map(candidate => (
                    <Typography
                      key={candidate.userId}
                      variant='body1'
                      component='div'
                      sx={{ fontWeight: 700, textAlign: 'left' }}
                    >
                      {candidate.displayName}
                    </Typography>
                  ))}
                </Box>

                <Typography variant='body2' component='div'>
                  {candidatesOutro}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Alert>

      {hasMultipleCandidates && (
        <TextField
          select
          required
          label='Wer war verantwortlich?'
          value={responsibleUserId || ''}
          onChange={event => onResponsibleChange(event.target.value || null)}
          fullWidth
          size='small'
          disabled={disabled || isLoadingCandidates}
          error={isSelectionMissing}
          helperText={
            isSelectionMissing
              ? 'Bitte auswählen, wen ihr befragt / zur Verantwortung zieht.'
              : 'Die genannten Mitarbeiter:innen sollen befragt werden, einer davon war verantwortlich.'
          }
        >
          {candidateList.map(candidate => (
            <MenuItem key={candidate.userId} value={candidate.userId}>
              {candidate.displayName}
            </MenuItem>
          ))}
        </TextField>
      )}

      {hasSingleCandidate && (
        <Typography variant='caption' color='text.secondary'>
          Verantwortlich gemäß Protokoll: <strong>{candidateList[0].displayName}</strong>
        </Typography>
      )}

      <TextField
        required
        label='Ursache des Versäumnisses'
        placeholder='Was hat dazu geführt, dass dieser Schritt nicht erfolgt ist?'
        value={reason}
        onChange={event => onReasonChange(event.target.value)}
        fullWidth
        multiline
        minRows={2}
        size='small'
        disabled={disabled}
        error={Boolean(reason) && isReasonTooShort}
        helperText={
          isReasonTooShort
            ? `Bitte mindestens ${REASON_MIN_LENGTH} Zeichen, damit wir Muster erkennen und gezielt nachschulen können.`
            : ' '
        }
      />
    </Stack>
  );
}

export default ResolutionResponsibleFields;
